/**
 * Citations Dialog Message Types
 *
 * Defines message protocol between citations dialog and parent (commands.ts).
 */

import type { MetadataMapTarget } from "../services/metadataTypes";

export interface CitationDialogShapeContext {
    slideIndex: number; // 1-indexed for UI display
    shapeId: string;
    shapeType?: string;
    shapeName?: string;
}

export interface CitationDialogEntry {
    sourceContextId: string;
    type: string;
    mapsTo?: MetadataMapTarget[];
    payload?: Record<string, unknown>;
    createdAt?: string;
    updatedAt?: string;
}

// Dialog -> Parent
export type CitationsDialogToParentMessage =
    | { action: "ready" }
    | { action: "closed" };

// Parent -> Dialog
export type CitationsParentToDialogMessage =
    {
        action: "init";
        selectedText: string;
        shapeContext?: CitationDialogShapeContext;
        citations: CitationDialogEntry[];
        totalCitationsOnShape: number;
        matchedCitationCount: number;
    };

export function sendCitationsToDialog(
    dialog: Office.Dialog,
    message: CitationsParentToDialogMessage
): void {
    try {
        dialog.messageChild(JSON.stringify(message));
    } catch (error) {
        console.error("[CitationsDialogMessages] Failed to send message to dialog:", error);
    }
}

export function sendCitationsToParent(message: CitationsDialogToParentMessage): void {
    Office.context.ui.messageParent(JSON.stringify(message));
}

export function parseCitationsDialogMessage(
    messageString: string
): CitationsDialogToParentMessage | null {
    try {
        return JSON.parse(messageString) as CitationsDialogToParentMessage;
    } catch {
        console.error("[CitationsDialogMessages] Failed to parse dialog message:", messageString);
        return null;
    }
}

export function parseCitationsParentMessage(
    messageString: string
): CitationsParentToDialogMessage | null {
    try {
        return JSON.parse(messageString) as CitationsParentToDialogMessage;
    } catch {
        console.error("[CitationsDialogMessages] Failed to parse parent message:", messageString);
        return null;
    }
}
